import { queryKeys } from "@healthos/client-data";
import { type Capability, RULE_ACTIONS } from "@healthos/heartbeat-rules";
import type { api } from "@healthos/schemas";
import {
  ArrowsClockwise,
  Barbell,
  BellRinging,
  Binoculars,
  CalendarBlank,
  CalendarCheck,
  CheckCircle,
  CloudRain,
  Coffee,
  Drop,
  Flag,
  FlagBanner,
  FlagCheckered,
  Footprints,
  ForkKnife,
  HandsClapping,
  Headphones,
  Heartbeat,
  HouseLine,
  Lightning,
  Moon,
  MoonStars,
  Pause,
  PersonSimpleWalk,
  Pill,
  Snowflake,
  Sparkle,
  SunHorizon,
  Timer,
  Tray,
  UsersThree,
  Wind,
} from "@phosphor-icons/react";
import type { QueryClient } from "@tanstack/react-query";
import type { NavigateFunction } from "react-router-dom";
import { client, jsonOrThrow } from "../../../api/healthos";
import { writeCohortConfig } from "../../../lib/demo-cohort-config";
import { clearAppLocalStorage } from "../../../lib/reset-local-state";

export type PersonaId = string;
export type LifeContext = "default" | "low_agency";
export type RouteContext = "patient" | "ops";
export type StepStatus = "pending" | "running" | "done" | "error";

export interface WhoamiResponse {
  userId: string;
  firstName: string | null;
  email: string | null;
  persona: PersonaId | null;
  lifeContext: LifeContext;
  onboardingComplete: boolean;
  demoExperienceDay?: number | null;
}

export interface ProgressEntry {
  label: string;
  status: StepStatus;
}

export type HeartbeatWhyResponse = api.MeHeartbeatWhyResponse;
export type ArmPerformanceResponse = api.MeArmPerformanceResponse;
export type DemoHeartbeatStoryResponse = api.DemoHeartbeatStoryResponse;
export type DemoHeartbeatStoryId = api.DemoHeartbeatStoryRequest["story"];

export interface PersonaMeta {
  short: string;
  demoName: string;
}

export const PERSONA_META: Record<string, PersonaMeta> = {
  sleep_recovery: { short: "Sleep", demoName: "James" },
  glp1_companion: { short: "GLP-1", demoName: "Maria" },
  cardiometabolic_partner: { short: "Cardio", demoName: "Carol" },
};

export type SignalPreset =
  | "big_walk"
  | "low_movement"
  | "packed_calendar"
  | "stress_stack"
  | "short_sleep"
  | "protein_meal"
  | "hydration"
  | "low_fluids"
  | "bp_spike"
  | "glucose_spike"
  | "medication_dose"
  | "missed_dose"
  | "high_sodium";

export const DAY_OPTIONS = [1, 2, 3, 4, 5, 6, 7] as const;

export const SIGNAL_PRESETS: Array<{
  id: SignalPreset;
  label: string;
  title: string;
  Icon: typeof Moon;
}> = [
  { id: "big_walk", label: "Big walk", title: "Add a high-step movement signal", Icon: Footprints },
  {
    id: "low_movement",
    label: "Low movement",
    title: "Add a low-activity day signal",
    Icon: Footprints,
  },
  {
    id: "packed_calendar",
    label: "Full calendar",
    title: "Add a packed workday of calendar events",
    Icon: CalendarBlank,
  },
  {
    id: "stress_stack",
    label: "Stress stack",
    title: "Add calendar load, low HRV, and elevated resting heart rate",
    Icon: Lightning,
  },
  { id: "short_sleep", label: "Short sleep", title: "Add short sleep and low HRV", Icon: Moon },
  {
    id: "protein_meal",
    label: "Protein meal",
    title: "Add a protein-forward meal",
    Icon: ForkKnife,
  },
  { id: "hydration", label: "Hydration", title: "Add a hydration log", Icon: Drop },
  { id: "low_fluids", label: "Low fluids", title: "Add a low hydration signal", Icon: Drop },
  {
    id: "bp_spike",
    label: "BP spike",
    title: "Add an elevated blood pressure reading",
    Icon: Heartbeat,
  },
  {
    id: "glucose_spike",
    label: "Glucose spike",
    title: "Add an elevated post-meal glucose signal",
    Icon: ForkKnife,
  },
  { id: "medication_dose", label: "Dose", title: "Add a medication dose signal", Icon: Pill },
  {
    id: "missed_dose",
    label: "Missed dose",
    title: "Add a scheduled dose that was not taken",
    Icon: Pill,
  },
  {
    id: "high_sodium",
    label: "High sodium",
    title: "Add a high sodium nutrition signal",
    Icon: ForkKnife,
  },
];

export const LENS_FILL_OPTIONS: Array<{
  id: api.DevFillLens;
  label: string;
  title: string;
  Icon: typeof Moon;
}> = [
  {
    id: "glp1",
    label: "GLP-1",
    title: "Seed GLP-1 dose tracking plus sleep, meal, and hydration signals",
    Icon: Pill,
  },
  {
    id: "sleep",
    label: "Sleep",
    title: "Seed sleep and HRV signals",
    Icon: Moon,
  },
  {
    id: "cardio",
    label: "Cardio",
    title: "Seed BP, HRV, sodium, and movement signals",
    Icon: Heartbeat,
  },
  {
    id: "stress",
    label: "Stress",
    title: "Seed calendar load, HRV, and resting heart rate signals",
    Icon: Lightning,
  },
  {
    id: "movement",
    label: "Move",
    title: "Seed steps, BP, HRV, and sodium signals",
    Icon: Footprints,
  },
  {
    id: "nutrition",
    label: "Food",
    title: "Seed meal and sodium signals",
    Icon: ForkKnife,
  },
  {
    id: "hydration",
    label: "Hydrate",
    title: "Seed hydration signals",
    Icon: Drop,
  },
  {
    id: "connection",
    label: "Connection",
    title: "Seed social connection log entries",
    Icon: UsersThree,
  },
];

export function lensFillLabel(lens: api.DevFillLens): string {
  return LENS_FILL_OPTIONS.find((option) => option.id === lens)?.label ?? lens;
}

/**
 * Fall back to a humanised slug when a seedable persona doesn't have
 * an explicit demo polish entry. "metabolic_reversal" -> "Metabolic
 * Reversal" / demo name "Demo".
 */
export function personaMeta(slug: string): PersonaMeta {
  if (PERSONA_META[slug]) return PERSONA_META[slug];
  const short = slug
    .split("_")
    .map((s) => {
      const head = s.charAt(0);
      return head ? head.toUpperCase() + s.slice(1) : s;
    })
    .join(" ");
  return { short, demoName: "Demo" };
}

/** What a destructive panel action is about to do, for the in-panel confirm. */
export function destructiveDemoMessage(label: string): string {
  return `${label} resets this demo user's onboarding, signals, briefs, content completions, and community activity. Continue?`;
}

/* ─────────────────────────────────────────────────────────────────
 * Scenario primitives: one async runner per scene. Each takes a
 * small API surface and reports per-step progress through `report`.
 * ───────────────────────────────────────────────────────────────── */

export interface ScenarioApi {
  qc: QueryClient;
  navigate: NavigateFunction;
  report: (steps: ProgressEntry[]) => void;
  /** Signed-in demo user's sub; null until whoami loads. */
  userId: string | null;
}

export async function seedPersona(persona: PersonaId, lifeContext: LifeContext): Promise<void> {
  await jsonOrThrow(
    await client().api.me["dev-seed"].$post({ json: { persona, lifeContext } }),
    "POST /api/me/dev-seed",
  );
}

export async function devReset(): Promise<void> {
  await jsonOrThrow(await client().api.me["dev-reset"].$post(), "POST /api/me/dev-reset");
}

export async function seedOpsCohort(): Promise<void> {
  await jsonOrThrow(
    await client().api.me["dev-seed-ops-cohort"].$post(),
    "POST /api/me/dev-seed-ops-cohort",
  );
}

export async function setDemoDay(day: number): Promise<api.DemoExperienceDayResponse> {
  return jsonOrThrow<api.DemoExperienceDayResponse>(
    await client().api.me["demo-day"].$post({
      json: { day, generateBrief: false },
    }),
    "POST /api/me/demo-day",
  );
}

export async function generateBrief(userId: string): Promise<void> {
  await jsonOrThrow(
    await client().api.simulation.brief.$post({
      json: { userId, timezone: "UTC" },
    }),
    "POST /api/simulation/brief",
  );
}

export async function addDemoSignal(
  preset: SignalPreset,
  day: number | null | undefined,
  options: { tick?: boolean } = {},
): Promise<api.DemoSignalResponse> {
  return jsonOrThrow<api.DemoSignalResponse>(
    await client().api.me["demo-signal"].$post({
      json: { preset, ...(day ? { day } : {}), tick: options.tick ?? false },
    }),
    "POST /api/me/demo-signal",
  );
}

/** `lensFillMutation`'s variable shape: `days` is optional on the wire
 * (server defaults to 6), but every call site below passes the panel's
 * current toggle value explicitly so the mutation's `variables` always
 * reflects what was actually requested. */
export interface LensFillVariables {
  lens: api.DevFillLens;
  days?: api.DevFillDays;
}

export async function fillLensDemoData(
  lens: api.DevFillLens,
  days?: api.DevFillDays,
): Promise<api.DevFillLensResponse> {
  return jsonOrThrow<api.DevFillLensResponse>(
    await client().api.me["dev-fill-lens"].$post({
      json: days ? { lens, days } : { lens },
    }),
    "POST /api/me/dev-fill-lens",
  );
}

/** "Seed everything" — every lens except glp1 in one write. */
export async function fillAllLensesDemoData(
  days?: api.DevFillDays,
): Promise<api.DevFillAllLensesResponse> {
  return jsonOrThrow<api.DevFillAllLensesResponse>(
    await client().api.me["dev-fill-all-lenses"].$post({
      json: days ? { days } : {},
    }),
    "POST /api/me/dev-fill-all-lenses",
  );
}

/**
 * Run Heartbeat now. With `force`, run it as an operator: ignore the attention
 * cooldown and the quiet gates.
 *
 * Without it a tick inside the 120-minute cooldown can only come back quiet,
 * which is exactly when someone is trying to watch a decision happen. The other
 * forced path injects a synthetic 5.4h sleep reading to get there; this one
 * decides on the signals the member actually has.
 */
export async function forceHeartbeatTick(
  userId: string | null | undefined,
  force = false,
): Promise<api.SimulationProxyResponse> {
  if (!userId) throw new Error("No demo user is loaded yet");
  return jsonOrThrow<api.SimulationProxyResponse>(
    await client().api.simulation.tick.$post({
      json: { userId, reason: "demo-panel", force },
    }),
    "POST /api/simulation/tick",
  );
}

// Only a moment puts something in front of the member.
const WIDGET_ACTIONS = RULE_ACTIONS;

/**
 * What a forced tick actually did, for the panel's result line.
 *
 * The forced path bypasses quiet, cooldown and hold-back, and the agent can
 * still choose an action that surfaces nothing: a run that reported "Attention
 * card forced onto /today" had decided `plan_event.emit`, and /today stayed
 * empty. Bypassing the gates is not the same as producing a card.
 */
export function describeTickOutcome(response: api.SimulationProxyResponse): string {
  const outcome = response.outcome as Record<string, unknown> | null | undefined;
  const action = typeof outcome?.action === "string" ? outcome.action : null;
  if (!action) return "Heartbeat stayed quiet, no card";
  if (WIDGET_ACTIONS.has(action)) return `Surfaced ${action}`;
  return `Heartbeat chose ${action}, which surfaces nothing`;
}

/**
 * Injects a short-sleep signal and forces a heartbeat tick with the
 * simulation flags (bypassQuiet, bypassCooldown, forceSurface), so the
 * pipeline MUST surface its decision as the featured attention event.
 * A plain forced tick can still decide "quiet"; this path cannot.
 */
export async function forceAttentionEvent(
  userId: string | null | undefined,
): Promise<api.SimulationProxyResponse> {
  if (!userId) throw new Error("No demo user is loaded yet");
  return jsonOrThrow<api.SimulationProxyResponse>(
    await client().api.simulation.signals.$post({
      json: {
        userId,
        kind: "wearable.sleep",
        source: "demo-panel",
        payload: { hours: 5.4, efficiency: 0.71, deep_minutes: 58 },
        tick: true,
      },
    }),
    "POST /api/simulation/signals",
  );
}

export async function readHeartbeatWhy(): Promise<HeartbeatWhyResponse> {
  return jsonOrThrow<HeartbeatWhyResponse>(
    await client().api.me["heartbeat-why"].$get(),
    "GET /api/me/heartbeat-why",
  );
}

export async function readArmPerformance(): Promise<ArmPerformanceResponse> {
  return jsonOrThrow<ArmPerformanceResponse>(
    await client().api.me["arm-performance"].$get(),
    "GET /api/me/arm-performance",
  );
}

export async function runDemoHeartbeatStory(
  story: DemoHeartbeatStoryId,
): Promise<DemoHeartbeatStoryResponse> {
  return jsonOrThrow<DemoHeartbeatStoryResponse>(
    await client().api.me["demo-heartbeat-story"].$post({ json: { story } }),
    "POST /api/me/demo-heartbeat-story",
  );
}

/** Presentation only: the rung and the id come from the engine's own ladder. */
export const DEMO_CAPABILITIES: Array<{
  capability: Capability;
  label: string;
  detail: string;
  Icon: typeof Moon;
  /** Stage the walk with the podcast episode one walk a day carries. */
  withEpisode?: true;
}> = [
  {
    capability: "recovery",
    label: "Inject recovery",
    detail: "a Reset in the next gap",
    Icon: Wind,
  },
  {
    capability: "idle",
    label: "Break a long sit",
    detail: "idle detection, no movement in two hours",
    Icon: Footprints,
  },
  {
    capability: "weather_window",
    label: "Rain window",
    detail: "rain clearing within the hour, calendar clear after it",
    Icon: CloudRain,
  },
  {
    capability: "back_to_back_prep",
    label: "Before a run",
    detail: "three or more meetings start within fifteen minutes",
    Icon: CalendarBlank,
  },
  {
    capability: "between_meetings",
    label: "Between meetings",
    detail: "a short gap inside a run",
    Icon: Timer,
  },
  {
    capability: "decent_gap",
    label: "A real gap",
    detail: "twenty to forty minutes before the next meeting",
    Icon: Pause,
  },
  {
    capability: "next_break_late",
    label: "Long stretch",
    detail: "no proper break for three hours",
    Icon: Coffee,
  },
  {
    capability: "workout_slot",
    label: "Workout slot",
    detail: "nothing logged, room in the day",
    Icon: Barbell,
  },
  {
    capability: "workout_evening",
    label: "Evening session",
    detail: "nothing logged, evening open",
    Icon: Barbell,
  },
  {
    capability: "glp1_small_meal",
    label: "GLP-1 small meal",
    detail: "low appetite reported, within two days of the dose",
    Icon: ForkKnife,
  },
  {
    capability: "glp1_sip_water",
    label: "GLP-1 sip water",
    detail: "nausea reported, dose day or the day after",
    Icon: Drop,
  },
  {
    capability: "glp1_strength",
    label: "GLP-1 strength",
    detail: "clear of the dose, no workout today",
    Icon: Barbell,
  },
  {
    capability: "close_the_gap_indoor",
    label: "Close it indoors",
    detail: "within reach of the rank above, weather against you",
    Icon: HouseLine,
  },
  {
    capability: "being_caught",
    label: "Being caught",
    detail: "the rank below is closing in",
    Icon: Binoculars,
  },
  {
    capability: "daylight_window",
    label: "Daylight window",
    detail: "sunset within the hour, no walk outdoors yet",
    Icon: SunHorizon,
  },
  {
    capability: "walk",
    label: "Fit a walk",
    detail: "under 1,000 steps by early afternoon, sized to the next gap",
    Icon: PersonSimpleWalk,
  },
  {
    capability: "walk",
    label: "Walk with a listen",
    detail: "the same walk, with a podcast episode for the way",
    Icon: Headphones,
    withEpisode: true,
  },
  {
    capability: "wind_down",
    label: "Wind down",
    detail: "the evening after a day that ran hot or back-to-back",
    Icon: Moon,
  },
  {
    capability: "heavy_tomorrow",
    label: "Heavy tomorrow",
    detail: "the evening before a day booked heavier than today",
    Icon: MoonStars,
  },
  {
    capability: "pushed_on_short_sleep",
    label: "Pushed on short sleep",
    detail: "the evening after a big step day on a night under target",
    Icon: Moon,
  },
  {
    capability: "protect",
    label: "Protect time",
    detail: "hold a block before it matters",
    Icon: CalendarCheck,
  },
  {
    capability: "restructure",
    label: "Restructure",
    detail: "propose moving one meeting",
    Icon: ArrowsClockwise,
  },
  {
    capability: "streak_save",
    label: "Save a streak",
    detail: "a streak that ends tonight unless something is logged",
    Icon: Lightning,
  },
  {
    capability: "streak_freeze",
    label: "Use a freeze",
    detail: "a freeze is banked and the day is already lost",
    Icon: Snowflake,
  },
  {
    capability: "close_the_gap",
    label: "Close the gap",
    detail: "one short walk from passing the member ahead",
    Icon: Flag,
  },
  {
    capability: "challenge_starting",
    label: "Challenge starting",
    detail: "a challenge the member is enrolled in opens today",
    Icon: FlagBanner,
  },
  {
    capability: "challenge_underway",
    label: "Challenge underway",
    detail: "teammates are walking a challenge the member has not joined",
    Icon: FlagBanner,
  },
  {
    capability: "reengage",
    label: "Re-engage",
    detail: "nothing has fired for three days, so point at the community",
    Icon: UsersThree,
  },
  {
    capability: "teammate_win",
    label: "Cheer a teammate",
    detail: "someone on the team just hit something worth a cheer",
    Icon: HandsClapping,
  },
  {
    capability: "group_route_complete",
    label: "Challenge complete",
    detail: "the member's group walked the whole company route together",
    Icon: FlagCheckered,
  },
];

export interface DemoMomentCardVariables {
  capability: string;
  withEpisode?: boolean;
}

/** One key per entry, since a walk is listed both plain and with a listen. */
export function demoCapabilityKey(entry: { capability: string; withEpisode?: boolean }): string {
  return entry.withEpisode ? `${entry.capability}+episode` : entry.capability;
}

export async function runDemoMomentCard(
  variables: DemoMomentCardVariables,
): Promise<api.DemoMomentCardResponse> {
  return jsonOrThrow<api.DemoMomentCardResponse>(
    await client().api.me["demo-rules-card"].$post({ json: variables }),
    "POST /api/me/demo-rules-card",
  );
}

export async function refetch(
  qc: QueryClient,
  ...keys: ReadonlyArray<readonly unknown[]>
): Promise<void> {
  await Promise.all(keys.map((k) => qc.invalidateQueries({ queryKey: k, refetchType: "all" })));
}

export interface Scenario {
  id: string;
  context: RouteContext;
  Icon: typeof Moon;
  label: string;
  hint: string;
  /**
   * Whether running this throws away demo state. True for anything that
   * reseeds or wipes, which is most of them, and those get a confirmation.
   * A scenario that only reveals something already there sets this false: a
   * confirm on a harmless button trains people to click through the one that
   * is about to delete their setup.
   */
  destructive?: boolean;
  /**
   * Hidden unless the build is DEV. For scenarios whose effect depends on a
   * `import.meta.env.DEV` branch elsewhere: shipped to the tweaks-gated panel
   * they do their destructive half and none of their useful half.
   */
  devOnly?: boolean;
  run(api: ScenarioApi): Promise<void>;
}

/**
 * Step helper. Each scenario builds a small list of named steps then
 * runs them through `track`, which mutates status in place and reports
 * progress to the panel UI. Named consts (vs array indexing) avoid the
 * non-null assertions a typed-tuple-with-!-cast pattern would force.
 */
function makeStep(label: string): ProgressEntry {
  return { label, status: "pending" };
}

async function track(
  step: ProgressEntry,
  steps: ProgressEntry[],
  report: (s: ProgressEntry[]) => void,
  fn: () => void | Promise<void>,
): Promise<void> {
  step.status = "running";
  report([...steps]);
  await fn();
  step.status = "done";
  report([...steps]);
}

export const SCENARIOS: Scenario[] = [
  {
    id: "member.maria.glp1",
    context: "patient",
    Icon: Sparkle,
    label: "Maria · GLP-1",
    hint: "Sunday afternoon, day 4 of dose week, nausea window opening",
    async run({ qc, navigate, report }) {
      const seed = makeStep("Seeding GLP-1…");
      const refresh = makeStep("Refreshing /today…");
      const steps = [seed, refresh];
      report(steps);
      await track(seed, steps, report, () => seedPersona("glp1_companion", "default"));
      await track(refresh, steps, report, async () => {
        navigate("/");
        await refetch(qc, queryKeys.ambient, queryKeys.score);
      });
    },
  },
  {
    id: "member.marcus.survival",
    context: "patient",
    Icon: Moon,
    label: "Marcus · survival",
    hint: "New father, fragmented sleep: the wedge moment",
    async run({ qc, navigate, report }) {
      const seed = makeStep("Seeding sleep (survival)…");
      const refresh = makeStep("Refreshing /today…");
      const steps = [seed, refresh];
      report(steps);
      await track(seed, steps, report, () => seedPersona("sleep_recovery", "low_agency"));
      await track(refresh, steps, report, async () => {
        navigate("/");
        await refetch(qc, queryKeys.ambient, queryKeys.score);
      });
    },
  },
  {
    id: "member.checkin.show",
    context: "patient",
    Icon: CheckCircle,
    label: "Show check-in",
    hint: "Forces the Thrive Score Questionnaire card onto /today, even when it is not due",
    destructive: false,
    /*
     * Not dev-only: /today honours the override for anyone the tweaks flag
     * enables (`useTweaksPanelAccess`), which is the same gate that shows this
     * panel. Finishing the forced card writes a real check-in, which is what a
     * tester wants and what the flag is scoped for.
     */
    async run({ report }) {
      const flag = makeStep("Forcing the check-in card…");
      const refresh = makeStep("Reloading /today…");
      const steps = [flag, refresh];
      report(steps);
      await track(flag, steps, report, async () => {
        sessionStorage.setItem("demo:force-pulse", "1");
        /*
         * The card rests for the day once the member says "Not today" or
         * finishes the five, and it remembers that in localStorage keyed by
         * day. Without clearing it the override puts the slot back and the
         * card still stays hidden, which is a worse failure than the gate it
         * replaces: the button appears to do nothing at all.
         */
        localStorage.removeItem("healthos_checkin_snoozed_day");
      });
      /*
       * A reload rather than a navigate, and it is the clearing above that
       * needs it. `pulseRested` is React state seeded once from that storage
       * key, and navigating to a route the page is already on does not remount
       * it, so the stale `true` survives and the card stays hidden: the exact
       * failure the clear exists to prevent. The flag is in sessionStorage and
       * outlives the reload.
       */
      await track(refresh, steps, report, async () => {
        window.location.assign("/");
      });
    },
  },
  {
    id: "member.force.attention",
    context: "patient",
    Icon: BellRinging,
    label: "Force a moment",
    hint: "Injects a short-sleep signal and force-surfaces the moment Heartbeat decides on",
    async run({ qc, navigate, report, userId }) {
      const inject = makeStep("Injecting signal + forced tick…");
      const refresh = makeStep("Refreshing /today…");
      const steps = [inject, refresh];
      report(steps);
      await track(inject, steps, report, async () => {
        await forceAttentionEvent(userId);
      });
      await track(refresh, steps, report, async () => {
        navigate("/");
        await refetch(qc, queryKeys.ambient, queryKeys.score);
      });
    },
  },
  {
    id: "member.fresh",
    context: "patient",
    Icon: ArrowsClockwise,
    label: "Fresh user",
    hint: "Wipes data and routes to onboarding",
    async run({ qc, navigate, report }) {
      const wipe = makeStep("Wiping data…");
      const route = makeStep("Routing to onboarding…");
      const steps = [wipe, route];
      report(steps);
      await track(wipe, steps, report, async () => {
        await devReset();
        clearAppLocalStorage();
        qc.clear();
      });
      await track(route, steps, report, () => navigate("/onboarding", { replace: true }));
    },
  },
  {
    id: "ops.cohort.populate",
    context: "ops",
    Icon: Sparkle,
    label: "Ops · populate cohort",
    hint: "Seed a favorable cohort of synthetic members: bands, migration, triage, engagement all fill in.",
    async run({ qc, navigate, report }) {
      const seed = makeStep("Seeding a favorable pilot cohort…");
      const set = makeStep("Setting book: 8,000 lives @ $612…");
      const refresh = makeStep("Refreshing dashboard…");
      const steps = [seed, set, refresh];
      report(steps);
      await track(seed, steps, report, () => seedOpsCohort());
      await track(set, steps, report, () => writeCohortConfig({ headcount: 8000, pmpm: 612 }));
      await track(refresh, steps, report, async () => {
        navigate("/operations/dashboard");
        await refetch(qc, ["operations"]);
      });
    },
  },
  {
    id: "ops.live.quiet",
    context: "ops",
    Icon: CheckCircle,
    label: "Ops · quiet day",
    hint: "Live cohort, calm queue: the operator's normal Monday",
    async run({ qc, navigate, report }) {
      const set = makeStep("Setting cohort: 5,000 lives @ $612…");
      const refresh = makeStep("Refreshing dashboard…");
      const steps = [set, refresh];
      report(steps);
      await track(set, steps, report, () => writeCohortConfig({ headcount: 5000, pmpm: 612 }));
      await track(refresh, steps, report, async () => {
        navigate("/operations/dashboard");
        await refetch(qc, ["operations"]);
      });
    },
  },
  {
    id: "ops.live.busy",
    context: "ops",
    Icon: Lightning,
    label: "Ops · busy queue",
    hint: "12k cohort, queue earning its keep: the enterprise pitch",
    async run({ qc, navigate, report }) {
      const set = makeStep("Setting cohort: 12,000 lives @ $612…");
      const refresh = makeStep("Refreshing dashboard…");
      const steps = [set, refresh];
      report(steps);
      await track(set, steps, report, () => writeCohortConfig({ headcount: 12000, pmpm: 612 }));
      await track(refresh, steps, report, async () => {
        navigate("/operations/dashboard");
        await refetch(qc, ["operations"]);
      });
    },
  },
  {
    id: "ops.prospect",
    context: "ops",
    Icon: Tray,
    label: "Ops · prospect",
    hint: "Pre-deploy view: what the buyer sees before enrolment",
    async run({ qc, navigate, report }) {
      const set = makeStep("Setting cohort: 9,000 lives @ $612…");
      const refresh = makeStep("Refreshing dashboard…");
      const steps = [set, refresh];
      report(steps);
      await track(set, steps, report, () => writeCohortConfig({ headcount: 9000, pmpm: 612 }));
      await track(refresh, steps, report, async () => {
        navigate("/operations/dashboard");
        await refetch(qc, ["operations"]);
      });
    },
  },
];

/* ───────────────────────────────────────────────────────────────── */
